import React, { useState } from "react";
import classNames from "classnames";
import { Icon } from "@/components/ui/display/Icon";
import { getInitials } from "@/utils/textUtils";
import type { AvatarProps } from "./types";
import styles from "./Avatar.module.css";

/**
 * Avatar component - Displays a user avatar image or initials.
 * Ported from Vue UserAvatar.vue component.
 */
export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt,
      name,
      size = "md",
      clickable = false,
      onClick,
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [imageError, setImageError] = useState(false);

    const handleClick = () => {
      if (clickable && onClick) {
        onClick();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === "Enter" || e.key === " ") && clickable) {
        e.preventDefault();
        onClick?.();
      }
    };

    const handleImageError = () => {
      setImageError(true);
    };

    const showImage = src && !imageError;
    const initials = name ? getInitials(name) : null;

    return (
      <div
        ref={ref}
        className={classNames(
          styles.avatar,
          styles[size],
          {
            [styles.clickable]: clickable,
          },
          className,
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        data-testid={testId}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt || name || "Avatar"}
            className={styles.image}
            onError={handleImageError}
          />
        ) : initials ? (
          <span className={styles.initials}>{initials}</span>
        ) : (
          // Fallback user icon
          <Icon name="account" className={styles.fallback} aria-hidden />
        )}
      </div>
    );
  },
);

Avatar.displayName = "Avatar";

export default Avatar;
